import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Link,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import Brightness2Icon from "@mui/icons-material/Brightness2";
import CodeIcon from "@mui/icons-material/Code";
import { motion, AnimatePresence } from "framer-motion";

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const NavbarBox = styled(motion.header)(({ theme, scrolled }) => ({
  position: "fixed",
  top: scrolled ? "15px" : "0px",
  left: "50%",
  transform: "translateX(-50%)",
  width: scrolled ? "calc(100% - 40px)" : "100%",
  maxWidth: scrolled ? "1200px" : "100%",
  zIndex: 1000,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: scrolled ? theme.spacing(1, 3) : theme.spacing(2, 6),
  backgroundColor: theme.palette.mode === "dark" 
    ? (scrolled ? "rgba(16, 28, 50, 0.8)" : "transparent")
    : (scrolled ? "rgba(255, 255, 255, 0.8)" : "transparent"),
  backdropFilter: scrolled ? "blur(16px)" : "none",
  border: scrolled 
    ? (theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)")
    : "none",
  borderRadius: scrolled ? "20px" : "0px",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: scrolled ? "0 10px 30px rgba(0,0,0,0.2)" : "none",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5, 3),
    width: "100%",
    top: 0,
    borderRadius: 0,
    border: "none",
    borderBottom: scrolled ? (theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)") : "none",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(10, 25, 47, 0.9)" : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(12px)",
  },
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const NavLink = styled(Link)(({ theme }) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  textDecoration: "none",
  padding: "8px 16px",
  borderRadius: "10px",
  position: "relative",
  transition: "all 0.3s ease",
  "&:hover": {
    color: theme.palette.primary.main,
    backgroundColor: "rgba(0, 229, 255, 0.05)",
  },
}));

const MobileMenu = styled(motion.div)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100vh",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(10, 25, 47, 0.98)" : "rgba(255, 255, 255, 0.98)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(4),
  zIndex: 1100,
  backdropFilter: "blur(10px)",
}));

const Navbar = ({ toggleTheme }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkClick = (e, target) => {
    e.preventDefault();
    setMenuOpen(false);
    const element = document.querySelector(target);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const navItems = [
    { label: "Home", href: "#Home" },
    { label: "About", href: "#About" },
    { label: "Story", href: "#Story" },
    { label: "Skills", href: "#Skills" },
    { label: "Experience", href: "#Resume" },
    { label: "Projects", href: "#Projects" },
    { label: "Contact", href: "#Contact" },
  ];

  return (
    <>
      <NavbarBox scrolled={scrolled}>
        <Link 
          href="#Home" 
          underline="none"
          onClick={(e) => handleLinkClick(e, '#Home')}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <motion.div
            whileHover={{ rotate: 180 }}
            transition={{ duration: 0.5 }}
          >
            <CodeIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          </motion.div>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: 1,
              background: "linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            SARTHAK
          </Typography>
        </Link>

        {/* Desktop Nav */}
        {!isMobile && (
          <NavLinks>
            {navItems.map((item) => (
              <NavLink key={item.label} href={item.href} onClick={(e) => handleLinkClick(e, item.href)}>
                {item.label}
              </NavLink>
            ))}
            <Box sx={{ ml: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={toggleTheme} color="primary" size="small">
                {theme.palette.mode === "dark" ? <WbSunnyIcon /> : <Brightness2Icon />}
              </IconButton>
              <Button
                variant="contained"
                href="./assets/FSD_SARTHAKSINGH.pdf"
                target="_blank"
                size="small"
                sx={{
                  borderRadius: "8px",
                  boxShadow: "0 4px 14px 0 rgba(0, 229, 255, 0.39)",
                }}
              >
                Resume
              </Button>
            </Box>
          </NavLinks>
        )}

        {/* Mobile Toggle */}
        {isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
             <IconButton onClick={toggleTheme} color="primary">
                {theme.palette.mode === "dark" ? <WbSunnyIcon /> : <Brightness2Icon />}
              </IconButton>
            <IconButton onClick={() => setMenuOpen(true)} color="primary">
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {menuOpen && (
            <MobileMenu
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
            >
              <IconButton 
                onClick={() => setMenuOpen(false)} 
                sx={{ position: "absolute", top: 20, right: 20 }}
                color="primary"
              >
                <CloseIcon fontSize="large" />
              </IconButton>
              
              {navItems.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    href={item.href}
                    onClick={(e) => handleLinkClick(e, item.href)}
                    sx={{
                      fontSize: "2rem",
                      fontWeight: 700,
                      color: "text.primary",
                      textDecoration: "none",
                      "&:hover": { color: "primary.main" }
                    }}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              
              <Button
                variant="contained"
                href="./assets/FSD_SARTHAKSINGH.pdf"
                target="_blank"
                size="large"
                sx={{ mt: 4, px: 6 }}
                onClick={() => setMenuOpen(false)}
              >
                Download Resume
              </Button>
            </MobileMenu>
          )}
        </AnimatePresence>
      </NavbarBox>
    </>
  );
};

export default Navbar;
